import React from 'react';
import { AlertTriangle, Building, Users, MapPin } from 'lucide-react';

const OrganizationDeleteModal = ({ organization, onConfirm, onCancel }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-ui-green-bg text-ui-green-fg';
      case 'inactive':
        return 'bg-ui-hover text-ui-ink';
      default:
        return 'bg-ui-blue-bg text-ui-blue-fg';
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 h-full w-full z-[70] flex items-center justify-center">
      <div className="bg-ui-surface rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-ui-red-bg rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-ui-red-fg" />
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-ui-ink">Delete Organization</h3>
              <p className="text-sm text-ui-muted">This action cannot be undone</p>
            </div>
          </div>

          {/* Organization Details */}
          <div className="bg-ui-sunk rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 bg-ui-blue-bg rounded-full flex items-center justify-center">
                  <Building className="w-4 h-4 text-ui-link" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-ui-ink truncate">
                    {organization.name}
                  </p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(organization.status || 'active')}`}>
                    <span className="capitalize">{organization.status || 'Active'}</span>
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center text-xs text-ui-muted">
                    <MapPin className="w-3 h-3 mr-1" />
                    <span className="truncate">{organization.id}</span>
                  </div>
                  {organization.admin_user_ids && (
                    <div className="flex items-center text-xs text-ui-muted">
                      <Users className="w-3 h-3 mr-1" />
                      <span className="truncate">{organization.admin_user_ids.length} admin(s)</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className="bg-ui-red-bg border border-ui-red-line rounded-md p-3 mb-4">
            <div className="flex items-start">
              <AlertTriangle className="w-4 h-4 text-ui-red-fg mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-ui-red-fg">
                <p className="font-medium mb-1">Warning:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>This will permanently remove the organization from the system</li>
                  <li>All users in this organization will lose access</li>
                  <li>All trips, components, and data will be permanently deleted</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-ui-control rounded-md text-sm font-medium text-ui-ink-2 bg-ui-surface hover:bg-ui-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ui-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-ui-on-primary bg-ui-red-strong hover:bg-ui-red-strong/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ui-red-solid transition-colors"
            >
              Delete Organization
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDeleteModal;